<script setup lang="ts">
/**
 * Create or edit one FAQ entry.
 *
 * Answer is a plain `<textarea>`, deliberately not `RichTextEditor` — FAQ
 * answers stay plain text, unlike Legal's `content`. See `ContentPane.vue`'s
 * own notes on why Legal is the one field that got the rich-text treatment.
 */
import { ref } from 'vue';

import AdminModal from './AdminModal.vue';
import { adminApi, type FaqRow } from './admin-api';
import { ApiError } from '@/net/api';

const props = defineProps<{ row: FaqRow | null }>();
const emit = defineEmits<{ close: []; saved: [] }>();

const question = ref(props.row?.question ?? '');
const answer = ref(props.row?.answer ?? '');

const saving = ref(false);
const error = ref<string | null>(null);

async function save(): Promise<void> {
  const q = question.value.trim();
  const a = answer.value.trim();
  if (!q || !a) {
    error.value = 'Question and answer are both required.';
    return;
  }

  saving.value = true;
  error.value = null;
  try {
    if (props.row) {
      await adminApi.faqPatch(props.row.id, { question: q, answer: a });
    } else {
      await adminApi.faqCreate({ question: q, answer: a });
    }
    emit('saved');
  } catch (caught) {
    error.value = caught instanceof ApiError ? caught.message : 'Could not reach the server.';
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <AdminModal :title="row ? 'Edit FAQ entry' : 'Add FAQ entry'" @close="emit('close')">
    <p v-if="error" class="bad">{{ error }}</p>

    <label class="field">
      <span>Question</span>
      <input v-model="question" type="text" data-test="faq-modal-question" />
    </label>
    <label class="field" style="margin-top: 0.6rem">
      <span>Answer</span>
      <textarea v-model="answer" spellcheck="false" data-test="faq-modal-answer"></textarea>
    </label>

    <template #footer>
      <button type="button" @click="emit('close')">Cancel</button>
      <button type="button" class="accent" :disabled="saving" @click="save">
        {{ saving ? 'Saving…' : 'Save' }}
      </button>
    </template>
  </AdminModal>
</template>
